import React, { useState, useRef } from 'react';
import { Button, Input } from '@/components/atoms';
import { createCollaboratorSchema, type CreateCollaborator } from '@/schemas/collaborators';
import {
  useCreateCollaboratorMutation,
  useUpdateCollaboratorMutation,
  useUploadAttachmentMutation,
} from '@/hooks/useErpApi';
import type { Collaborator } from '@/schemas/collaborators';

interface CollaboratorFormProps {
  initialData?: Collaborator;
  onSuccess?: () => void;
  onCancel?: () => void;
}

export const CollaboratorForm: React.FC<CollaboratorFormProps> = ({
  initialData,
  onSuccess,
  onCancel,
}) => {
  const { mutateAsync: createCollaborator, isLoading: createLoading } = useCreateCollaboratorMutation();
  const { mutate: updateCollaborator, isLoading: updateLoading } = useUpdateCollaboratorMutation();
  const { mutateAsync: uploadAttachment } = useUploadAttachmentMutation();
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [pendingLogoFile, setPendingLogoFile] = useState<File | null>(null);
  const [logoUploading, setLogoUploading] = useState(false);
  const [createdCollaboratorId, setCreatedCollaboratorId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isLoading = createLoading || updateLoading || logoUploading;

  const [formData, setFormData] = useState<CreateCollaborator>({
    company_name: initialData?.company_name || '',
    contact_person: initialData?.contact_person || '',
    contact_number: initialData?.contact_number || '',
    email: initialData?.email || '',
    gst_number: initialData?.gst_number || '',
    pan_number: initialData?.pan_number || '',
    bank_name: initialData?.bank_name || '',
    bank_account_no: initialData?.bank_account_no || '',
    bank_ifsc: initialData?.bank_ifsc || '',
    experience: initialData?.experience || '',
    logo: initialData?.logo || '',
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    // Transform GST number to uppercase
    const transformedValue = name === 'gst_number' ? value.toUpperCase() : value;
    setFormData((prev) => ({ ...prev, [name]: transformedValue }));
    if (validationErrors[name]) {
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setValidationErrors({});

    try {
      // Convert empty strings to undefined for optional fields
      const cleanedData: any = {
        ...formData,
        email: formData.email?.trim() || undefined,
        bank_name: formData.bank_name?.trim() || undefined,
        bank_account_no: formData.bank_account_no?.trim() || undefined,
        bank_ifsc: formData.bank_ifsc?.trim() || undefined,
        gst_number: formData.gst_number?.trim().toUpperCase() || '',
      };
      
      // Handle logo field
      if (initialData) {
        // Update mode: include logo if it exists (actual download_url from upload or existing logo)
        if (formData.logo && formData.logo.trim()) {
          cleanedData.logo = formData.logo.trim();
        } else {
          cleanedData.logo = undefined;
        }
      } else {
        // Create mode: don't include logo (will be uploaded separately after creation)
        cleanedData.logo = undefined;
      }
      const validated = createCollaboratorSchema.parse(cleanedData);

      if (initialData) {
        await updateCollaborator({ id: initialData.id, data: validated });
        onSuccess?.();
      } else {
        // Create new collaborator
        const response = await createCollaborator(validated);
        // Response is ApiResponse<CollaboratorResponse>, so data contains the collaborator
        const collaborator = (response as any)?.data || response;
        const newCollaboratorId = collaborator?.id;
        
        if (newCollaboratorId) {
          setCreatedCollaboratorId(newCollaboratorId);
          
          // Step 2: Upload logo if one was selected
          if (pendingLogoFile) {
            try {
              setLogoUploading(true);
              const uploadResponse = await uploadAttachment({
                entityType: 'logo',
                entityId: newCollaboratorId,
                file: pendingLogoFile,
              });
              
              // Update form state with logo URL from response
              // Response is ApiResponse<AttachmentResponse>
              const attachment = (uploadResponse as any)?.data || uploadResponse;
              if (attachment?.download_url) {
                setFormData((prev) => ({ ...prev, logo: attachment.download_url }));
              }
              
              // Clear pending file
              setPendingLogoFile(null);
            } catch (uploadError) {
              // Log error but don't fail - collaborator was created successfully
              console.error('Logo upload failed:', uploadError);
              setError('Collaborator created successfully, but logo upload failed. You can upload it later.');
            } finally {
              setLogoUploading(false);
            }
          }
        }
        
        onSuccess?.();
      }
    } catch (err) {
      if (err && typeof err === 'object' && 'errors' in err) {
        const zodError = err as { errors: Array<{ path: (string | number)[]; message: string }> };
        const errMap: Record<string, string> = {};
        zodError.errors.forEach((e) => {
          const field = String(e.path[0]);
          errMap[field] = e.message;
        });
        setValidationErrors(errMap);
      } else {
        setError(err instanceof Error ? err.message : 'An error occurred');
      }
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {error && (
        <div className="bg-error-50 border border-error-200 rounded-lg p-4">
          <p className="text-error-700">{error}</p>
        </div>
      )}

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label htmlFor="company_name" className="block text-sm font-medium text-neutral-700 mb-2">
            Company Name *
          </label>
          <Input
            id="company_name"
            name="company_name"
            value={formData.company_name}
            onChange={handleChange}
            placeholder="Enter company name"
            disabled={isLoading}
          />
          {validationErrors.company_name && (
            <p className="text-error-600 text-sm mt-1">{validationErrors.company_name}</p>
          )}
        </div>

        <div>
          <label
            htmlFor="contact_person"
            className="block text-sm font-medium text-neutral-700 mb-2"
          >
            Contact Person *
          </label>
          <Input
            id="contact_person"
            name="contact_person"
            value={formData.contact_person}
            onChange={handleChange}
            placeholder="Enter contact person"
            disabled={isLoading}
          />
          {validationErrors.contact_person && (
            <p className="text-error-600 text-sm mt-1">{validationErrors.contact_person}</p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label
            htmlFor="contact_number"
            className="block text-sm font-medium text-neutral-700 mb-2"
          >
            Contact Number *
          </label>
          <Input
            id="contact_number"
            name="contact_number"
            value={formData.contact_number}
            onChange={handleChange}
            placeholder="10-digit phone number"
            disabled={isLoading}
          />
          {validationErrors.contact_number && (
            <p className="text-error-600 text-sm mt-1">{validationErrors.contact_number}</p>
          )}
        </div>

        <div>
          <label htmlFor="email" className="block text-sm font-medium text-neutral-700 mb-2">
            Email
          </label>
          <Input
            id="email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="Enter email address"
            disabled={isLoading}
          />
          {validationErrors.email && (
            <p className="text-error-600 text-sm mt-1">{validationErrors.email}</p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label htmlFor="gst_number" className="block text-sm font-medium text-neutral-700 mb-2">
            GST Number *
          </label>
          <Input
            id="gst_number"
            name="gst_number"
            value={formData.gst_number}
            onChange={handleChange}
            placeholder="Enter GST number"
            disabled={isLoading}
            required
          />
          {validationErrors.gst_number && (
            <p className="text-error-600 text-sm mt-1">{validationErrors.gst_number}</p>
          )}
        </div>

        <div>
          <label htmlFor="pan_number" className="block text-sm font-medium text-neutral-700 mb-2">
            PAN Number
          </label>
          <Input
            id="pan_number"
            name="pan_number"
            value={formData.pan_number}
            onChange={handleChange}
            placeholder="Enter PAN number"
            disabled={isLoading}
          />
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div>
          <label htmlFor="bank_name" className="block text-sm font-medium text-neutral-700 mb-2">
            Bank Name
          </label>
          <Input
            id="bank_name"
            name="bank_name"
            value={formData.bank_name}
            onChange={handleChange}
            placeholder="Enter bank name"
            disabled={isLoading}
          />
        </div>

        <div>
          <label
            htmlFor="bank_account_no"
            className="block text-sm font-medium text-neutral-700 mb-2"
          >
            Account Number
          </label>
          <Input
            id="bank_account_no"
            name="bank_account_no"
            value={formData.bank_account_no}
            onChange={handleChange}
            placeholder="Enter account number"
            disabled={isLoading}
          />
          {validationErrors.bank_account_no && (
            <p className="text-error-600 text-sm mt-1">{validationErrors.bank_account_no}</p>
          )}
        </div>

        <div>
          <label htmlFor="bank_ifsc" className="block text-sm font-medium text-neutral-700 mb-2">
            IFSC Code
          </label>
          <Input
            id="bank_ifsc"
            name="bank_ifsc"
            value={formData.bank_ifsc}
            onChange={handleChange}
            placeholder="e.g., SBIN0001234"
            disabled={isLoading}
          />
          {validationErrors.bank_ifsc && (
            <p className="text-error-600 text-sm mt-1">{validationErrors.bank_ifsc}</p>
          )}
        </div>
      </div>

      <div>
        <label htmlFor="experience" className="block text-sm font-medium text-neutral-700 mb-2">
          Experience
        </label>
        <Input
          id="experience"
          name="experience"
          value={formData.experience}
          onChange={handleChange}
          placeholder="Years of experience"
          disabled={isLoading}
        />
      </div>

      {/* Logo Upload Section */}
      <div>
        <label className="block text-sm font-medium text-neutral-700 mb-2">Company Logo</label>
        
        {initialData?.id || createdCollaboratorId ? (
          // Edit mode or after creation - show file input with upload button
          <div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  setPendingLogoFile(file);
                  // Create preview URL for immediate display
                  const previewUrl = URL.createObjectURL(file);
                  setFormData((prev) => ({ ...prev, logo: previewUrl }));
                }
                // Reset input to allow re-selecting the same file
                e.target.value = '';
              }}
            />
            
            <div className="flex items-center gap-3">
              <Button
                type="button"
                variant="secondary"
                onClick={() => fileInputRef.current?.click()}
                disabled={isLoading || logoUploading}
              >
                Select Logo File
              </Button>
              
              {pendingLogoFile && (
                <Button
                  type="button"
                  variant="primary"
                  onClick={async () => {
                    if (!pendingLogoFile) return;
                    
                    const collaboratorId = initialData?.id || createdCollaboratorId;
                    if (!collaboratorId) return;
                    
                    try {
                      setLogoUploading(true);
                      setError(null);
                      
                      // POST /api/v1/attachments - Upload logo
                      const uploadResponse = await uploadAttachment({
                        entityType: 'logo',
                        entityId: collaboratorId,
                        file: pendingLogoFile,
                      });
                      
                      // Extract download_url from response
                      const attachment = (uploadResponse as any)?.data || uploadResponse;
                      const downloadUrl = attachment?.download_url;
                      
                      if (downloadUrl) {
                        // Update form state with logo URL
                        setFormData((prev) => ({ ...prev, logo: downloadUrl }));
                        setPendingLogoFile(null);
                      } else {
                        throw new Error('No download URL in upload response');
                      }
                    } catch (uploadError) {
                      console.error('Logo upload failed:', uploadError);
                      setError('Failed to upload logo. Please try again.');
                    } finally {
                      setLogoUploading(false);
                    }
                  }}
                  disabled={isLoading || logoUploading || !pendingLogoFile}
                >
                  {logoUploading ? 'Uploading...' : 'Upload Logo'}
                </Button>
              )}
            </div>
            
            {pendingLogoFile && (
              <div className="mt-3 p-3 border border-neutral-200 rounded-lg">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <p className="text-xs text-neutral-500 mb-2">Selected logo:</p>
                    <img
                      src={formData.logo}
                      alt="Logo preview"
                      className="h-16 w-auto object-contain"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                    <p className="text-xs text-neutral-600 mt-2">{pendingLogoFile.name}</p>
                  </div>
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                      setPendingLogoFile(null);
                      setFormData((prev) => ({ ...prev, logo: initialData?.logo || '' }));
                      if (fileInputRef.current) {
                        fileInputRef.current.value = '';
                      }
                    }}
                    disabled={isLoading || logoUploading}
                  >
                    Remove
                  </Button>
                </div>
              </div>
            )}
            
            {formData.logo && !pendingLogoFile && (
              <div className="mt-3 p-3 border border-neutral-200 rounded-lg">
                <p className="text-xs text-neutral-500 mb-2">Current logo:</p>
                <img
                  src={formData.logo}
                  alt="Company logo"
                  className="h-16 w-auto object-contain"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                />
              </div>
            )}
            
            <p className="text-xs text-neutral-500 mt-1">
              Upload a company logo image (PNG, JPG, SVG). Max size: 10MB
            </p>
          </div>
        ) : (
          // Create mode - allow file selection before creation
          <div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  setPendingLogoFile(file);
                  // Create preview URL for immediate display
                  const previewUrl = URL.createObjectURL(file);
                  setFormData((prev) => ({ ...prev, logo: previewUrl }));
                }
                // Reset input to allow re-selecting the same file
                e.target.value = '';
              }}
            />
            
            {pendingLogoFile ? (
              <div className="mt-3 p-3 border border-neutral-200 rounded-lg">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <p className="text-xs text-neutral-500 mb-2">Selected logo (will be uploaded after creation):</p>
                    <img
                      src={formData.logo}
                      alt="Logo preview"
                      className="h-16 w-auto object-contain"
                    />
                    <p className="text-xs text-neutral-600 mt-2">{pendingLogoFile.name}</p>
                  </div>
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                      setPendingLogoFile(null);
                      setFormData((prev) => ({ ...prev, logo: '' }));
                      if (fileInputRef.current) {
                        fileInputRef.current.value = '';
                      }
                    }}
                    disabled={isLoading}
                  >
                    Remove
                  </Button>
                </div>
              </div>
            ) : (
              <div className="bg-neutral-50 border border-neutral-200 rounded-lg p-4">
                <p className="text-sm text-neutral-600 mb-3">
                  Select a logo file to upload after creating the collaborator
                </p>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isLoading}
                >
                  Select Logo File
                </Button>
              </div>
            )}
            
            <p className="text-xs text-neutral-500 mt-1">
              Logo will be uploaded automatically after the collaborator is created
            </p>
          </div>
        )}
      </div>

      <div className="flex gap-2 justify-end">
        {onCancel && (
          <Button type="button" variant="secondary" onClick={onCancel} disabled={isLoading}>
            Cancel
          </Button>
        )}
        <Button type="submit" variant="primary" disabled={isLoading}>
          {isLoading ? 'Saving...' : initialData ? 'Update' : 'Create'} Collaborator
        </Button>
      </div>
    </form>
  );
};
